// AppStateContext — Phase G1.
//
// Shares App.jsx state + handlers with the file-based route tree
// introduced in Phase G1 (TanStack Router). Routes under src/routes/
// read state via the `useAppState()` hook instead of receiving 20+
// props through the old AppRouter switch.
//
// Phase G3 will gradually push state DOWN into the routes that need
// it (or into purpose-built hooks like useActiveSession from
// src/App/). This context is the migration bridge — broad initially,
// trimmed as call sites move off it.
//
// CRITICAL: This context is only mounted INSIDE the gate ladder in
// App.jsx (after auth/registration/onboarding/placement/mascot
// gates pass). Route components must never assume the gates have
// run for them — they only render when the user is in the
// "everything OK, show a screen" state.

import { createContext, useContext, type ReactNode } from "react";

// Permissive `any`-shaped state during the Phase G migration window.
// AppState carries 30+ fields from App.jsx (state setters + handlers
// + derived values + cloud-sync writers) and the precise shape
// changes each PR as state migrates into purpose-built hooks. G3
// will tighten this as call sites move off the broad context.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppState = any;

const AppStateContext = createContext<AppState | null>(null);

export function AppStateProvider({
  value,
  children,
}: {
  value: AppState;
  children: ReactNode;
}) {
  return (
    <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
  );
}

/** Hook consumed by every route component under src/routes/. Throws
 * loudly if called outside the provider — would mean a route mounted
 * outside the gate ladder, which is a bug. */
export function useAppState(): AppState {
  const ctx = useContext(AppStateContext);
  if (!ctx) {
    throw new Error(
      "useAppState() called outside <AppStateProvider>. " +
        "Routes can only render after App.jsx's gate ladder passes.",
    );
  }
  return ctx;
}

/**
 * P-PATHHOME-BUG-2-ROUND-2 — the canonical child id for ALL
 * child_attempts reads (mastery, skill graph, trail, today's mission,
 * decision evidence). Returns the active child's cloud students.id
 * (`activeStudentId`), NOT the local activeChildId — because that is
 * the id the persisted attempt rows are keyed by (FK + RLS target).
 *
 * Null when the active child isn't linked to a cloud student yet:
 * reads then return empty (no rows exist for an unlinked kid — correct),
 * and the first WRITE lazy-creates + links via ensureActiveStudentId,
 * after which this flips non-null and the reads populate.
 *
 * Single source so the engine-wide swap is grep-able and consistent —
 * every engine read site calls this instead of reaching for the local
 * id under three different names (activeChildId / activeKid.id /
 * pal.kid.id).
 */
export function useEngineChildId(): string | null {
  const app = useAppState();
  return (app?.activeStudentId ?? null) as string | null;
}

/**
 * MA-93 (ADR-043) — the active child's REGISTRY age (in years): the
 * `registry.children[].age` row keyed by `activeChildId`, finite-guarded,
 * else null.
 *
 * MA-80 — this is ONE of the two inputs to the ONE age source, never an age
 * source on its own. Every serve/tree/session surface (the four session
 * screens, SkillTree, SkillDetail, Journey, the story/reading-level screens,
 * onboarding parent-results, Parent Settings, the mission pick) reads
 * `resolveSelectionAge(app.progress.studentAge, activeChildAge(app))`
 * (useTodaysMission.ts): the progress age wins when finite, this registry age
 * is the fallback. Reading it alone goes stale after a cross-device age
 * change (App.jsx's cloud merge writes the progress age only).
 */
export function activeChildAge(app: AppState): number | null {
  const children = (app?.registry?.children ?? []) as Array<{
    id: string;
    age?: number | null;
  }>;
  const a = children.find((c) => c.id === app?.activeChildId)?.age;
  return typeof a === "number" && Number.isFinite(a) ? a : null;
}
